import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { LeaderboardEntry } from '@/types';
import PageSpinner from '@/components/PageSpinner';
import { PageHeaderSkeleton, TableSkeleton } from '@/components/PageSkeletons';
import { billingService, matchService } from '@/services/storageService';
import { buildAvatarDataUri } from '@/lib/avatarBuilder';
import StarRating from '@/components/StarRating';
import PackageFeatureGate from '@/components/PackageFeatureGate';
import { useI18n } from '@/contexts/I18nContext';

export default function Leaderboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { activeClubId, currentClubRole } = useClub();
  const clubId = activeClubId ? Number(activeClubId) : null;
  const billingQuery = useQuery({
    queryKey: ['billing-status', clubId],
    queryFn: () => billingService.getStatus(clubId),
    enabled: Boolean(user && clubId),
  });
  const resolvedBillingPackageCode = String(
    (billingQuery.data as any)?.package ||
      (billingQuery.data as any)?.recommended_package ||
      'package1',
  )
    .trim()
    .toLowerCase();
  const isStarterPackage = resolvedBillingPackageCode === 'package1';
  const isEvaluationsFeatureAvailable = !isStarterPackage;
  const roleRaw = String(currentClubRole || user?.role || '').toLowerCase();
  const isAdminViewer = roleRaw === 'admin';
  const [isTouch, setIsTouch] = React.useState(false);
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(hover: none), (pointer: coarse)');
    const update = () => setIsTouch(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
      return () => mq.removeEventListener('change', update);
    }
    mq.addListener?.(update);
    return () => mq.removeListener?.(update);
  }, []);

  const leaderboardQuery = useQuery({
    queryKey: ['leaderboard', clubId],
    queryFn: () => matchService.getLeaderboard(clubId as number),
    enabled: Boolean(clubId),
  });

  const combinedRanking: LeaderboardEntry[] = React.useMemo(() => {
    const rawItems = Array.isArray((leaderboardQuery.data as any)?.items)
      ? (leaderboardQuery.data as any).items
      : Array.isArray(leaderboardQuery.data)
        ? leaderboardQuery.data
        : [];

    if (!Array.isArray(rawItems)) return [];
    return rawItems
      .map((item: any, idx: number) => {
        const id = String(item.id ?? item.member_id ?? '');
        if (!id) return null;
        return {
          id,
          firstName: item.first_name ?? item.firstName ?? '',
          lastName: item.last_name ?? item.lastName ?? '',
          role: item.role ? String(item.role).toLowerCase() : undefined,
          avatarSeed: item.avatar_seed ?? undefined,
          avatarCollection: item.collection_name ?? undefined,
          avatarName: item.avatar_name ?? undefined,
          avatarUrl: buildAvatarDataUri(item.collection_name, item.avatar_seed),
          played: Number(item.played ?? 0),
          won: Number(item.won ?? 0),
          lost: Number(item.lost ?? 0),
          scored: Number(item.scored ?? 0),
          against: Number(item.against ?? 0),
          pd: Number(item.pd ?? 0),
          pes: Number(item.pes ?? 0),
          mps: Number(item.mps ?? 0),
          confidence: Number(item.confidence ?? 0),
          rating: Number(item.rating ?? 0),
          hitCap: Boolean(item.hit_cap ?? item.hitCap),
          finalRank: Number(item.final_rank ?? item.finalRank ?? idx + 1),
        } as LeaderboardEntry;
      })
      .filter(Boolean) as LeaderboardEntry[];
  }, [leaderboardQuery.data]);

  const isMe = (id: string) => (user ? id === user.id : false);
  const improvementLabel = (p: LeaderboardEntry) => (p.rating - p.pes).toFixed(1);
  const isAboveEvaluation = (p: LeaderboardEntry) =>
    isEvaluationsFeatureAvailable && p.pes > 0 && p.rating > p.pes;
  const isBelowEvaluation = (p: LeaderboardEntry) =>
    isEvaluationsFeatureAvailable && p.pes > 0 && p.rating < p.pes;

  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredRanking = React.useMemo(() => {
    if (!searchTerm.trim()) return combinedRanking;
    const query = searchTerm.toLowerCase();
    return combinedRanking.filter((p) => `${p.firstName} ${p.lastName}`.toLowerCase().includes(query));
  }, [combinedRanking, searchTerm]);

  // Pagination
  const [page, setPage] = React.useState(1);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(filteredRanking.length / pageSize));
  const paged = filteredRanking.slice((page - 1) * pageSize, page * pageSize);

  React.useEffect(() => setPage(1), [searchTerm]);
  React.useEffect(() => setPage((p) => Math.min(p, totalPages)), [totalPages]);

  const emptyMessage =
    combinedRanking.length === 0
      ? t('leaderboard_empty_no_matches', 'No completed matches yet to build the leaderboard.')
      : t('leaderboard_empty_search', 'No players match your search.');

  const Badge = ({ children, className }: { children: React.ReactNode; className: string }) => (
    <span className={`text-[11px] px-2 py-0.5 rounded-full whitespace-nowrap ${className}`}>{children}</span>
  );

  const DeltaIndicator = ({ value, direction }: { value: string; direction: 'up' | 'down' }) => {
    const Icon = direction === 'up' ? ArrowUp : ArrowDown;
    const colorClass = direction === 'up' ? 'text-success' : 'text-destructive';
    const label = direction === 'up' ? `+${value}` : value;

    if (isTouch) {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <span className="inline-flex">
              <Icon className={`h-4 w-4 ${colorClass}`} aria-label={direction === 'up' ? t('leaderboard_rating_improved', 'Rating improved') : t('leaderboard_rating_decreased', 'Rating decreased')} />
            </span>
          </PopoverTrigger>
          <PopoverContent className="px-2 py-1 text-xs w-auto">{label}</PopoverContent>
        </Popover>
      );
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Icon className={`h-4 w-4 ${colorClass}`} aria-label={direction === 'up' ? t('leaderboard_rating_improved', 'Rating improved') : t('leaderboard_rating_decreased', 'Rating decreased')} />
            </span>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const PlayerCell = ({ p, highlight }: { p: LeaderboardEntry; highlight: boolean }) => (
    <div className="flex items-center gap-3 min-w-0">
      <Avatar className="h-9 w-9 shrink-0">
        {p.avatarUrl && <AvatarImage src={p.avatarUrl} alt={p.avatarName || `${p.firstName} ${p.lastName}`} />}
        <AvatarFallback>{`${p.firstName?.[0] || ''}${p.lastName?.[0] || ''}`}</AvatarFallback>
      </Avatar>

      <div className="min-w-0">
        <div className="font-semibold leading-none flex items-center gap-2 flex-wrap">
          <span className="truncate max-w-[180px] sm:max-w-[240px]">
            {p.firstName} {p.lastName}
          </span>

          {p.role === 'coach' && <Badge className="bg-blue-100 text-blue-800">{t('role_coach', 'Coach')}</Badge>}
          {p.role === 'admin' && <Badge className="bg-amber-100 text-amber-800">{t('role_admin', 'Admin')}</Badge>}
          {highlight && <Badge className="bg-primary text-primary-foreground">{t('leaderboard_you', 'You')}</Badge>}
        </div>
        <div className="text-xs text-muted-foreground">{t('leaderboard_rank', 'Rank')} #{p.finalRank}</div>
        <div className="mt-1">
          <StarRating value={isEvaluationsFeatureAvailable ? (p.pes ? p.pes / 20 : 0) : 0} size={12} />
        </div>
      </div>
    </div>
  );

  const StatPill = ({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) => (
    <div className="rounded-lg border bg-muted/30 px-3 py-2 flex items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className={`text-sm font-semibold ${className || ''}`}>{value}</span>
    </div>
  );

  const LockedColumnsPlaceholder = ({ compact = false }: { compact?: boolean }) => (
    <div className={`${compact ? 'w-full max-w-xl' : 'w-full'}`}>
      <PackageFeatureGate
        featureName={t('leaderboard_insights_title', 'Leaderboard Insights')}
        currentPackageCode={resolvedBillingPackageCode}
        availabilityText={t('leaderboard_insights_availability', 'Advanced leaderboard insights are available on Pro (Package 2) and Elite (Package 3).')}
        canUpgrade={isAdminViewer}
        onUpgrade={() => (window.location.href = '/dashboard/club')}
        contactAdminText={t('leaderboard_insights_contact_admin', 'Please contact your club admin to upgrade the package and unlock these leaderboard insights.')}
      />
    </div>
  );

  if (!clubId) {
    return <PageSpinner label={t('leaderboard_select_club', 'Select a club to view the leaderboard.')} />;
  }

  if (leaderboardQuery.isLoading) {
    return (
      <div className="space-y-6 animate-fade-in w-full px-3 sm:px-4 lg:px-6 overflow-x-hidden">
        <PageHeaderSkeleton />
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <TableSkeleton rows={8} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in w-full px-3 sm:px-4 lg:px-6 overflow-x-hidden">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground">{t('nav_leaderboard', 'Leaderboard')}</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          {t('leaderboard_subtitle', 'Rankings are based on rating (evaluation + match performance).')}
        </p>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:flex-wrap bg-sidebar-accent text-sidebar-accent-foreground">
          <CardTitle className="text-sidebar-accent-foreground">{t('nav_leaderboard', 'Leaderboard')}</CardTitle>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('leaderboard_search_placeholder', 'Search by player name')}
            className="sm:max-w-xs text-[hsl(var(--sidebar-accent))] placeholder:text-[hsl(var(--sidebar-accent))]"
          />
        </CardHeader>

        <CardContent className="space-y-4 overflow-hidden">
          {/* MOBILE */}
          <div className="space-y-3 lg:hidden">
            {isStarterPackage && paged.length > 0 && <LockedColumnsPlaceholder />}
            {paged.map((p) => {
              const highlight = isMe(p.id);
              return (
                <div
                  key={p.id}
                  className={['rounded-xl border p-4 bg-card', highlight ? 'ring-1 ring-primary/30 bg-primary/5' : ''].join(
                    ' ',
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <PlayerCell p={p} highlight={highlight} />
                    <div className="text-right shrink-0">
                      <div className="text-xs text-muted-foreground">{t('leaderboard_rating', 'Rating')}</div>
                      {isStarterPackage ? (
                        <div className="text-lg font-bold text-muted-foreground">-</div>
                      ) : (
                        <div className="text-lg font-bold inline-flex items-center gap-1">
                          {p.rating.toFixed(1)}
                          {isAboveEvaluation(p) && <DeltaIndicator value={improvementLabel(p)} direction="up" />}
                          {isBelowEvaluation(p) && <DeltaIndicator value={improvementLabel(p)} direction="down" />}
                          {p.hitCap && <Badge className="bg-blue-100 text-blue-800 ml-2">{t('leaderboard_reevaluate', 'Re-evaluate')}</Badge>}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    {/* <StatPill label="Eval (PES)" value={p.pes.toFixed(1)} />
                    <StatPill label="Match (MPS)" value={p.mps.toFixed(1)} /> */}
                    <StatPill label={t('leaderboard_games', 'Games')} value={isStarterPackage ? '-' : p.played} className={isStarterPackage ? 'text-muted-foreground' : undefined} />
                    <StatPill label={t('leaderboard_won', 'Won')} value={isStarterPackage ? '-' : p.won} className={isStarterPackage ? 'text-muted-foreground' : undefined} />
                    <StatPill label={t('leaderboard_lost', 'Lost')} value={isStarterPackage ? '-' : p.lost} className={isStarterPackage ? 'text-muted-foreground' : undefined} />
                    <StatPill label={t('leaderboard_points_earned', 'Points Earned')} value={isStarterPackage ? '-' : p.scored.toFixed(0)} className={isStarterPackage ? 'text-muted-foreground' : undefined} />
                    <StatPill label={t('leaderboard_points_lost', 'Points Lost')} value={isStarterPackage ? '-' : p.against.toFixed(0)} className={isStarterPackage ? 'text-muted-foreground' : undefined} />
                    <StatPill
                      label={t('leaderboard_pd', 'Point Difference (PD)')}
                      value={isStarterPackage ? '-' : p.pd.toFixed(0)}
                      className={isStarterPackage ? 'text-muted-foreground' : p.pd >= 0 ? 'text-success' : 'text-destructive'}
                    />
                  </div>
                </div>
              );
            })}

            {!paged.length && (
              <div className="rounded-lg border bg-muted/30 p-6 text-center text-muted-foreground">{emptyMessage}</div>
            )}
          </div>

          {/* DESKTOP */}
          <div className="hidden lg:block">
            <div className="overflow-x-auto">
              <Table className="min-w-full border">
                <TableHeader className="border-b font-bold text-base bg-sidebar-primary text-white [&_th]:text-white">
                  <TableRow>
                    <TableHead className="text-center border" rowSpan={2}>
                      {t('leaderboard_rank', 'Rank')}
                    </TableHead>
                    <TableHead className="min-w-[220px] pl-6 border" rowSpan={2}>
                      {t('leaderboard_player', 'Player')}
                    </TableHead>
                    <TableHead className="text-center whitespace-nowrap border" rowSpan={2}>
                      {t('leaderboard_rating', 'Rating')}
                    </TableHead>
                    {/* <TableHead className="text-center whitespace-nowrap" rowSpan={2}>Eval (PES)</TableHead>
                    <TableHead className="text-center whitespace-nowrap" rowSpan={2}>Match (MPS)</TableHead> */}
                    <TableHead className="text-center whitespace-nowrap border" colSpan={2}>
                      {t('leaderboard_games', 'Games')}
                    </TableHead>
                    <TableHead className="text-center whitespace-nowrap border" colSpan={2}>
                      {t('leaderboard_points', 'Points')}
                    </TableHead>
                    <TableHead className="text-center whitespace-nowrap border" rowSpan={2}>
                      {t('leaderboard_pd', 'Point Difference (PD)')}
                    </TableHead>
                  </TableRow>
                  <TableRow>
                    <TableHead className="text-center whitespace-nowrap border">{t('leaderboard_won', 'Won')}</TableHead>
                    <TableHead className="text-center whitespace-nowrap border">{t('leaderboard_lost', 'Lost')}</TableHead>
                    <TableHead className="text-center whitespace-nowrap border">{t('leaderboard_scored', 'Scored')}</TableHead>
                    <TableHead className="text-center whitespace-nowrap border">{t('leaderboard_against', 'Against')}</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {paged.map((p, index) => {
                    const highlight = isMe(p.id);
                    const hitCeiling = p.hitCap;
                    const rowClass = isStarterPackage
                      ? ''
                      : [
                          highlight ? 'bg-primary/10 ring-1 ring-primary/30' : '',
                          hitCeiling ? 'bg-blue-50 ring-1 ring-blue-200' : '',
                        ]
                          .filter(Boolean)
                          .join(' ');
                    const leadCellClass = isStarterPackage
                      ? [
                          highlight ? 'bg-primary/10' : '',
                          hitCeiling ? 'bg-blue-50' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')
                      : '';
                    return (
                      <TableRow key={p.id} className={rowClass || undefined}>
                        <TableCell className={`font-semibold text-center ${leadCellClass}`}>#{p.finalRank}</TableCell>
                        <TableCell className={`font-medium ${leadCellClass}`}>
                          <PlayerCell p={p} highlight={highlight} />
                        </TableCell>

                        {isStarterPackage ? (
                          index === 0 ? (
                            <TableCell className="border p-2 align-middle" colSpan={6} rowSpan={Math.max(1, paged.length)}>
                              <div
                                className="h-full w-full flex items-center justify-center"
                                style={{ minHeight: `${Math.max(1, paged.length) * 68}px` }}
                              >
                                <LockedColumnsPlaceholder compact />
                              </div>
                            </TableCell>
                          ) : null
                        ) : (
                          <>
                            <TableCell className="font-semibold text-center">
                            <div className="flex items-center justify-center gap-2">
                              {p.rating.toFixed(1)}
                              {isAboveEvaluation(p) && <DeltaIndicator value={improvementLabel(p)} direction="up" />}
                              {isBelowEvaluation(p) && <DeltaIndicator value={improvementLabel(p)} direction="down" />}
                              {p.hitCap && <Badge className="bg-blue-100 text-blue-800">{t('leaderboard_reevaluate', 'Re-evaluate')}</Badge>}
                            </div>
                            </TableCell>

                            {/* <TableCell className="text-center">{p.pes.toFixed(1)}</TableCell>
                            <TableCell className="text-center">{p.mps.toFixed(1)}</TableCell> */}
                            <TableCell className="text-center">{p.won}</TableCell>
                            <TableCell className="text-center">{p.lost}</TableCell>

                            <TableCell className="text-center">{p.scored.toFixed(0)}</TableCell>
                            <TableCell className="text-center">{p.against.toFixed(0)}</TableCell>
                            <TableCell className={`font-medium text-center ${p.pd >= 0 ? 'text-success' : 'text-destructive'}`}>
                              {p.pd.toFixed(0)}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}

                  {!paged.length && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        {emptyMessage}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t('leaderboard_showing', 'Showing')} {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, combinedRanking.length)} {t('leaderboard_of', 'of')} {combinedRanking.length}
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 rounded border disabled:opacity-50"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {t('common_prev', 'Prev')}
                </button>
                <span>
                  {t('common_page', 'Page')} {page} / {totalPages}
                </span>
                <button
                  className="px-3 py-1 rounded border disabled:opacity-50"
                  onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                  disabled={page >= totalPages}
                >
                  {t('common_next', 'Next')}
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
